import React, { useEffect, useState } from "react"
import { AlertMessage, AssetMap, Round, TeamPlayer, TeamRound } from "../../types"

import Header from "./Header"

interface DetailsProps {
  round: Round
  teamRound: TeamRound | null
  teamPlayers: TeamPlayer[]
  currentUserId: number
  assets: AssetMap
  showFAQ()
  enterScores()
  showAlert: (message: AlertMessage) => void
}

export default function Details({
  round,
  teamRound,
  teamPlayers,
  currentUserId,
  assets,
  showFAQ,
  enterScores,
  showAlert
}: DetailsProps) {
  const startsAt = Date.parse(round.startsAt)
  const msUntilStart: number = startsAt - new Date().valueOf()
  const [beforeRound, setBeforeRound] = useState(msUntilStart > 0)

  useEffect(() => {
    let id
    if (beforeRound) {
      id = setTimeout(() => {
        setBeforeRound(false)
      }, msUntilStart)
    }
    return () => {
      if (id) {
        clearTimeout(id)
      }
    }
  })

  let roundStartsAtText: string | null = null

  if (!round.cancelationText) {
    if (teamRound?.startTimeOfDay) {
      roundStartsAtText =
        round.startType === "shotgun"
          ? `Shotgun start at ${round.startTimeOfDay} on Hole ${teamRound.startHoleNumber}${teamRound.startGroupLabel}`
          : `${teamRound.startTimeOfDay} tee time on Hole ${teamRound.startHoleNumber}`
    } else {
      roundStartsAtText =
        round.startType === "shotgun"
          ? `Shotgun start at ${round.startTimeOfDay}`
          : `Tee times starting at ${round.startTimeOfDay}`
    }
  }

  return (
    <>
      <div className="screen-heading">
        <h1 className="screen-title">
          {round.startDay} {round.startMonthDay}
        </h1>
        <p>
          {round.course.name}
          <br />
          {roundStartsAtText}
        </p>
      </div>
      <div className="screen-body">
        <div className="super-card">
          <div className="card-v2 round-details-hero" style={{ marginBottom: "1rem" }}>
            <div className="container">
              <Header round={round} teamPlayers={teamPlayers} />
              <div className="register-widget">
                <div className="widget-action">
                  <DetailsContent
                    teamRound={teamRound}
                    teamPlayers={teamPlayers}
                    currentUserId={currentUserId}
                    beforeRound={beforeRound}
                    startsAt={startsAt}
                    round={round}
                    showFAQ={showFAQ}
                    enterScores={enterScores}
                    showAlert={showAlert}
                  />
                </div>
              </div>
            </div>
          </div>
          {teamRound && beforeRound && round.postRoundGathering ? (
            <div className="round-features-cards" style={{ marginBottom: "1rem" }}>
              <div className="card-v2 round-features-card">
                <div className="container">
                  <div className="card-content-wrapper">
                    <div className="card-icon">
                      <img src={assets.slapHands} alt="slap hands" />
                    </div>
                    <div className="card-content">
                      <h3>Post-round meetup</h3>
                      <p>{round.postRoundGathering.text}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </>
  )
}

interface DetailsContent {
  teamRound: TeamRound
  teamPlayers: TeamPlayer[]
  currentUserId: number
  beforeRound: boolean
  startsAt: number
  round: Round
  showFAQ()
  enterScores()
  showAlert: (message: AlertMessage) => void
}

function DetailsContent({
  teamRound,
  teamPlayers,
  currentUserId,
  beforeRound,
  startsAt,
  round,
  showFAQ,
  enterScores,
  showAlert
}: DetailsContent) {
  const currentUserTeamPlayer = teamPlayers.find((p) => p.id === currentUserId)

  if (!currentUserTeamPlayer || currentUserTeamPlayer.status !== "registered") {
    if (round.seriesType === "game") {
      return (
        <div style={{ marginBottom: "1rem" }}>You are not registered for this game and registration has closed.</div>
      )
    }

    return (
      <>
        <div style={{ marginBottom: "1rem" }}>
          You are not registered for this round and registration has closed. Register for future rounds before they fill
          up.
        </div>
        <a
          className="action-button"
          href={`/app/leagues/${round.league.id}/choose_rounds?back_path=${encodeURIComponent(
            `/app/league_rounds/${round.id}`
          )}`}
        >
          Register For Future Rounds
        </a>
      </>
    )
  }

  if (teamRound.complete || round.complete) {
    return (
      <span
        className="action-button border-only"
        onClick={() =>
          showAlert({
            title: "Round Complete",
            message: "You cannot change your scores once your round is complete",
            buttons: [{ text: "Ok", style: "primary" }]
          })
        }
      >
        Round Complete
      </span>
    )
  }

  if (round.cancelationText) {
    return <>{round.cancelationText}</>
  }

  if (beforeRound) {
    console.log(`Round starts at ${round.startsAt} ${startsAt}`)
    console.log(`Current client time is ${new Date()} ${new Date().valueOf()}`)
    console.log(`Current server time is ${round.currentTime} ${Date.parse(round.currentTime)}`)

    return (
      <>
        <a className="action-button" onClick={showFAQ}>
          View Instructions
        </a>
        <div style={{ marginTop: "1rem", fontSize: "0.875rem" }}>
          Scoring begins
          <Countdown round={round} startsAt={startsAt} />
        </div>
      </>
    )
  }

  return (
    <a className="action-button" onClick={enterScores}>
      Enter Scores
    </a>
  )
}

interface CountdownProps {
  round: Round
  startsAt: number
}

function Countdown({ round, startsAt }: CountdownProps) {
  const [secondsRemaining, setSecondsRemaining] = useState((startsAt - new Date().valueOf()) / 1000)

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsRemaining((s) => (startsAt - new Date().valueOf()) / 1000)
    }, 1000)
    return () => clearInterval(id)
  })

  let indexStart = 11

  if (secondsRemaining < 600) {
    indexStart = 15
  } else if (secondsRemaining < 3600) {
    indexStart = 14
  } else if (secondsRemaining < 36000) {
    indexStart = 12
  } else if (secondsRemaining > 86400) {
    return (
      <>
        {" "}
        at {round.startTimeOfDay} {round.startDay}
      </>
    )
  }

  return <> in {new Date(secondsRemaining * 1000).toISOString().substring(indexStart, 19)}</>
}
